import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import {
  createCalendarApi,
  createHolidayApi,
  deleteCalendarApi,
  deleteHolidayApi,
  getCalendarsApi,
  getHolidaysApi,
  updateCalendarApi,
  updateHolidayApi,
  type ICreateCalendarPayload,
  type ICreateHolidayPayload,
  type IUpdateCalendarPayload,
  type IUpdateHolidayPayload,
} from '../api/dataverse';
import {
  generateBufferDaysRuleId,
  getBufferDaysRulesApi,
  saveBufferDaysRulesApi,
  type ICreateBufferDaysRulePayload,
  type IUpdateBufferDaysRulePayload,
} from '../api/bufferDays';
import { getDeliverySettingsApi, saveDeliverySettingsApi } from '../api/localStorage';
import { queryKey } from '../constants';
import { IBufferDaysRule, IDeliverySettings } from '../types';

export const useCalendars = () => {
  return useQuery({
    queryKey: [queryKey.getCalendars],
    queryFn: getCalendarsApi,
  });
};

export const useCreateCalendar = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: ICreateCalendarPayload) => createCalendarApi(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [queryKey.getCalendars] });
      toast.success('Calendar created');
    },
  });
};

export const useUpdateCalendar = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: IUpdateCalendarPayload) => updateCalendarApi(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [queryKey.getCalendars] });
      toast.success('Calendar updated');
    },
  });
};

export const useDeleteCalendar = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteCalendarApi(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [queryKey.getCalendars] });
      toast.success('Calendar deleted');
    },
  });
};

export const useHolidays = (calendarId: string | undefined) => {
  return useQuery({
    queryKey: [queryKey.getHolidays, calendarId],
    queryFn: () => getHolidaysApi(calendarId as string),
    enabled: !!calendarId,
  });
};

/**
 * Resolves the calendar matching the current year (matched by `name`, e.g.
 * "2026") and fetches its holiday rules — self-healing: unlike a stored
 * `activeCalendarId` setting (which 404s forever once the calendar it points
 * to is deleted/recreated), this always finds whichever calendar is actually
 * named for the current year.
 */
export const useCurrentYearHolidays = () => {
  const currentYear = String(new Date().getFullYear());

  const calendarsQuery = useCalendars();
  const calendar = calendarsQuery.data?.find((c) => c.name === currentYear);

  const holidaysQuery = useHolidays(calendar?.id);

  return {
    calendar,
    holidays: holidaysQuery.data ?? [],
    isLoading: calendarsQuery.isLoading || holidaysQuery.isLoading,
    isFetching: calendarsQuery.isFetching || holidaysQuery.isFetching,
    error: calendarsQuery.error ?? holidaysQuery.error ?? null,
  };
};

export const useCreateHoliday = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: ICreateHolidayPayload) => createHolidayApi(payload),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: [queryKey.getHolidays, variables.calendarId] });
      toast.success('Holiday saved');
    },
  });
};

export const useUpdateHoliday = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: IUpdateHolidayPayload) => updateHolidayApi(payload),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: [queryKey.getHolidays, variables.calendarId] });
      toast.success('Holiday updated');
    },
  });
};

export const useDeleteHoliday = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id }: { id: string; calendarId: string }) => deleteHolidayApi(id),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: [queryKey.getHolidays, variables.calendarId] });
      toast.success('Holiday removed');
    },
  });
};

export const useDeliverySettings = () => {
  return useQuery({
    queryKey: [queryKey.getDeliverySettings],
    queryFn: getDeliverySettingsApi,
  });
};

export const useSaveDeliverySettings = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (settings: IDeliverySettings) => saveDeliverySettingsApi(settings),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [queryKey.getDeliverySettings] });
    },
  });
};

export const useBufferDaysRules = () => {
  return useQuery({
    queryKey: [queryKey.getBufferDaysRules],
    queryFn: async () => {
      try {
        return await getBufferDaysRulesApi();
      } catch (error: any) {
        // The blob (and its container file) doesn't exist until the first
        // rule is ever saved — that 404 just means "no rules yet", not a
        // real error, so swallow it here instead of surfacing the global
        // error toast.
        if (error?.response?.status === 404) return [];
        throw error;
      }
    },
  });
};

export const useCreateBufferDaysRule = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: ICreateBufferDaysRulePayload) => {
      const current = queryClient.getQueryData<IBufferDaysRule[]>([queryKey.getBufferDaysRules]) ?? [];
      const newRule: IBufferDaysRule = {
        ...payload,
        id: generateBufferDaysRuleId(),
        updated: new Date().toISOString(),
        deleted: false,
      };
      const next = [...current, newRule];
      // No existing data yet → POST creates the blob; otherwise PUT the whole array
      await saveBufferDaysRulesApi(next, current.length > 0);
      return next;
    },
    onSuccess: (next) => {
      queryClient.setQueryData([queryKey.getBufferDaysRules], next);
      toast.success('Buffer days configuration created');
    },
  });
};

export const useUpdateBufferDaysRule = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: IUpdateBufferDaysRulePayload) => {
      const current = queryClient.getQueryData<IBufferDaysRule[]>([queryKey.getBufferDaysRules]) ?? [];
      const next = current.map((rule) =>
        rule.id === payload.id ? { ...payload, updated: new Date().toISOString(), deleted: false } : rule,
      );
      await saveBufferDaysRulesApi(next, true);
      return next;
    },
    onSuccess: (next) => {
      queryClient.setQueryData([queryKey.getBufferDaysRules], next);
      toast.success('Buffer days configuration updated');
    },
  });
};

// Soft delete — the array stays the same length (POST always sends every
// record back), the row is just flagged so the UI can filter it out.
export const useDeleteBufferDaysRule = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, updatedBy }: { id: string; updatedBy?: string }) => {
      const current = queryClient.getQueryData<IBufferDaysRule[]>([queryKey.getBufferDaysRules]) ?? [];
      const next = current.map((rule) =>
        rule.id === id ? { ...rule, deleted: true, updated: new Date().toISOString(), updatedBy } : rule,
      );
      await saveBufferDaysRulesApi(next, true);
      return next;
    },
    onSuccess: (next) => {
      queryClient.setQueryData([queryKey.getBufferDaysRules], next);
      toast.success('Configuration removed');
    },
  });
};
